<script setup>
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue';
import { useDevicesList } from '@vueuse/core';
import { UsersIcon } from '@heroicons/vue/outline';
import CheckIcon from '@/Components/Icons/CheckIcon.vue';
import CrossIcon from '@/Components/Icons/CrossIcon.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { CAMERA_TYPES, DEVICES_KINDS } from '@/Utils/vice-constants';
import CameraPermissionBlockedIcon from '@/Components/Icons/CameraPermissionBlockedIcon.vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import ToggleSettings from '@/Components/Inputs/ToggleSettings.vue';

const props = defineProps([
  'isClinician',
  'highlighted',
  'sessionId',
  'useCases',
  'devices',
  'proceed',
]);
const emit = defineEmits(['precheckPassed', 'deviceSetupMatched', 'echoWarning', 'update:devices']);

const multiCameraDevicesStore = useLocalStreamingDevicesStore();
const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

const predefinedMic = props.devices?.audio?.find((d) => d.device_kind === 'audioinput');
const predefinedSpk = props.devices?.audio?.find((d) => d.device_kind === 'audiooutput');
const predefinedCam = props.devices?.video?.find((d) => d.device_type_label === 'Main Camera');

const {
  videoInputs: cameras,
  audioInputs: microphones,
  audioOutputs: speakers,
} = useDevicesList({
  requestPermissions: true,
});

const video = ref();
const audioStream = ref();
const micLabel = ref('');
const camLabel = ref('');
const spkLabel = ref('');
const micStatus = ref('');
const cameraStatus = ref('');
const speakerStatus = ref('');
const currentWidth = ref(0);
const loading = ref(false);
const showGuide = ref(false);
const micEnabled = ref(false);
const isUnmounted = ref(false);
const isSpeakerPlaying = ref(false);
const currentMicro = ref('empty');
const currentCamera = ref('empty');
const currentSpeaker = ref('empty');
const videoStream = ref(null);
const speakerTestOutput = ref(null);
const updatedDevices = { ...props.devices };

let javascriptNode = null;

const stopVideoStream = async () => {
  if (videoStream.value) {
    videoStream.value.getTracks().forEach((track) => {
      track.stop();
      videoStream.value.removeTrack(track);
    });
    videoStream.value = null;
  }
  if (video.value) {
    video.value.srcObject = null;
  }
};

const startVideoStream = async (deviceId) => {
  try {
    const constraints = {
      video: {
        aspectRatio: 16 / 9,
        deviceId: deviceId ? { exact: deviceId } : undefined,
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    videoStream.value = stream;

    if (video.value && !isUnmounted.value) {
      video.value.srcObject = stream;
    }

    return stream;
  } catch (error) {
    console.error('Error starting video stream:', error);
    throw error;
  }
};

const stopLiveStream = async (liveStream) => {
  if (liveStream.value?.active) {
    await liveStream.value.getTracks().forEach((track) => {
      track.stop();
      liveStream.value.removeTrack(track);
    });
    liveStream.value = null;
  }
};

watchEffect(() => {
  micEnabled.value = !!audioStream?.value?.active;
});

const setupWarnings = computed(() =>
  [micStatus.value, cameraStatus.value, speakerStatus.value].includes('warning'),
);
const audioInTheSameGroup = computed(() => !!checkIfAudioInTheSameGroup());

const areDevicesReady = computed(() => {
  // Check if we have actual active streams
  const hasActiveVideoStream =
    videoStream.value &&
    videoStream.value.active &&
    videoStream.value.getVideoTracks().length > 0 &&
    videoStream.value.getVideoTracks()[0].readyState === 'live';

  const hasActiveAudioStream =
    audioStream.value &&
    audioStream.value.active &&
    audioStream.value.getAudioTracks().length > 0 &&
    audioStream.value.getAudioTracks()[0].readyState === 'live';

  const statusesReady =
    ['warning', 'success'].includes(micStatus.value) &&
    ['warning', 'success'].includes(cameraStatus.value) &&
    ['warning', 'success'].includes(speakerStatus.value);

  return hasActiveVideoStream && hasActiveAudioStream && statusesReady && !loading.value;
});

const validateStreams = () => {
  const issues = [];

  // Video validation
  if (!videoStream.value) {
    issues.push('No video stream');
  } else if (!videoStream.value.active) {
    issues.push('Video stream not active');
  } else if (videoStream.value.getVideoTracks().length === 0) {
    issues.push('No video tracks');
  } else if (videoStream.value.getVideoTracks()[0].readyState !== 'live') {
    issues.push('Video track not live');
  }

  // Audio validation
  if (!audioStream.value) {
    issues.push('No audio stream');
  } else if (!audioStream.value.active) {
    issues.push('Audio stream not active');
  } else if (audioStream.value.getAudioTracks().length === 0) {
    issues.push('No audio tracks');
  } else if (audioStream.value.getAudioTracks()[0].readyState !== 'live') {
    issues.push('Audio track not live');
  }

  if (issues.length > 0) {
    console.error('Stream validation issues:', issues);
    return false;
  }

  return true;
};

// check if selected mic and speaker belong to the same physical device group (avoid echo)
const checkIfAudioInTheSameGroup = () => {
  if (
    currentMicro.value &&
    currentMicro.value !== 'empty' &&
    currentSpeaker.value &&
    currentSpeaker.value !== 'empty' &&
    microphones.value &&
    speakers.value
  ) {
    const micDevice = microphones.value.find((m) => m.deviceId === currentMicro.value);
    const spkDevice = speakers.value.find((s) => s.deviceId === currentSpeaker.value);

    if (!micDevice || !spkDevice) return true;

    // Primary check: same OS group ID
    if (micDevice.groupId === spkDevice.groupId) return true;

    // Fallback: Windows often splits embedded chipsets (e.g. Realtek) into
    // separate group IDs even though they're the same physical hardware.
    // If both labels share the same chipset name, treat them as co-located.
    const extractChipset = (label) => {
      const match = label?.match(/\(([^)]+)\)/);
      return match ? match[1].toLowerCase() : label?.toLowerCase() ?? '';
    };

    const micChipset = extractChipset(micDevice.label);
    const spkChipset = extractChipset(spkDevice.label);

    return micChipset && spkChipset && micChipset === spkChipset;
  }

  return true;
};

watchEffect(() => {
  if (areDevicesReady.value) {
    if (validateStreams() && !loading.value) {
      emit('precheckPassed', true);
    } else {
      console.error('Devices appear ready but validation failed');
    }

    // if predefined devices are not equal to selection
    if (props.isClinician) {
      if (setupWarnings.value) {
        const matches = [];
        // cam
        if (cameraStatus.value === 'warning' && predefinedCam) {
          matches.push({ new: camLabel.value, old: predefinedCam?.device_data?.label });
        }
        // mic
        if (micStatus.value === 'warning' && predefinedMic) {
          matches.push({ new: micLabel.value, old: predefinedMic?.device_data?.label });
        }
        // spk
        if (speakerStatus.value === 'warning' && predefinedSpk) {
          matches.push({ new: spkLabel.value, old: predefinedSpk?.device_data?.label });
        }

        emit('deviceSetupMatched', {
          status: false,
          matches,
        });
      } else {
        emit('deviceSetupMatched', { status: true });
      }
    }
  } else {
    emit('precheckPassed', false);
    emit('echoWarning', false);

    if (props.isClinician) {
      emit('deviceSetupMatched', { status: true });
    }
  }

  // echo warning
  if (audioInTheSameGroup.value) {
    emit('echoWarning', false);
  } else {
    emit('echoWarning', true);
  }
});

// watch(
//   () => setupWarnings.value,
//   (warningsExist) => {
//     emit('deviceSetupMatched', !warningsExist);
//   },
// );

watch(
  () => currentCamera.value,
  async (newCameraId) => {
    if (newCameraId === 'empty') return;

    cameraStatus.value = 'default';

    try {
      loading.value = true;

      await stopVideoStream();

      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });

      const stream = await startVideoStream(newCameraId);

      await new Promise((resolve) => {
        setTimeout(resolve, 300);
      });

      // Validate the stream is actually working
      if (stream && stream.active && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        if (track.readyState === 'live') {
          camLabel.value = stream.getVideoTracks()[0]?.label;
          // check if device same as predefined
          if (props.isClinician) {
            if (predefinedCam?.device_data?.label !== camLabel.value) {
              cameraStatus.value = 'warning';
            } else {
              cameraStatus.value = 'success';
            }
          } else {
            cameraStatus.value = 'success';
          }

          multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].cameraDeviceId =
            newCameraId;
        } else {
          cameraStatus.value = 'fail';
          throw new Error('Video track not live');
        }
      } else {
        cameraStatus.value = 'fail';
        throw new Error('Invalid video stream');
      }
    } catch (error) {
      console.error('Camera switch failed:', error);
      cameraStatus.value = 'fail';
    } finally {
      setTimeout(() => {
        loading.value = false;
      }, 1000);
    }
  },
);

watch(
  () => currentMicro.value,
  async (newMicId) => {
    if (newMicId === 'empty') return;

    micStatus.value = 'default';

    try {
      if (audioStream?.value?.active) {
        await stopLiveStream(audioStream);
      }

      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });

      // Start audio stream and validate
      await new Promise((resolve, reject) => {
        const constraints = { audio: { deviceId: { exact: newMicId } }, video: false };
        navigator.mediaDevices
          .getUserMedia(constraints)
          .then(async (currentStream) => {
            if (audioStream.value) {
              await stopLiveStream(audioStream);
            }

            audioStream.value = currentStream;

            setTimeout(() => {
              if (
                currentStream.active &&
                currentStream.getAudioTracks().length > 0 &&
                currentStream.getAudioTracks()[0].readyState === 'live'
              ) {
                micLabel.value = currentStream.getAudioTracks()[0]?.label;

                // update status and selection for device setup center
                if (props.isClinician) {
                  // update status
                  if (predefinedMic?.device_data?.label !== micLabel.value) {
                    micStatus.value = 'warning';
                  } else {
                    micStatus.value = 'success';
                  }
                } else {
                  micStatus.value = 'success';
                }

                multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].micDeviceId =
                  newMicId;

                // Setup audio visualization
                setupAudioVisualization(currentStream);
                resolve();
              } else {
                micStatus.value = 'fail';
                reject(new Error('Audio stream not ready'));
              }
            }, 300);
          })
          .catch(reject);
      });
    } catch (error) {
      console.error('Microphone switch failed:', error);
      micStatus.value = 'fail';
    }
  },
);

const saveDefaultDevice = (deviceId, deviceKind) => {
  const device =
    deviceKind === DEVICES_KINDS.AUDIO_INPUT
      ? microphones.value.find((mic) => mic.deviceId === deviceId)
      : speakers.value.find((spk) => spk.deviceId === deviceId);

  axios
    .post(route('clinician.set-default-device-for-session', props.sessionId), {
      device_kind: deviceKind,
      device_data: device,
    })
    .then(() => {
      // Update predefined audio pool if exist
      if (props.devices.audio.length) {
        const updatedDevice = updatedDevices.audio.find((d) => d.device_kind === deviceKind);
        updatedDevice.device_data = device;
        emit('update:devices', updatedDevices);
      }

      // add to pinia
      deviceAndSessionSetupStore.manageDefaultClinicianDevices(deviceKind, device);
    })
    .catch((e) => {
      console.log(e);
    });
};

const saveMainCameraDevice = (deviceId, deviceKind) => {
  const device = cameras.value.find((cam) => cam.deviceId === deviceId);
  const useCase = props.useCases.find((uc) => uc.name === 'General');
  const useCaseEssential = useCase.device_type_usage_priorities.find(
    (dtup) => dtup.name === 'Essential',
  );
  const slot = useCaseEssential.device_type_use_case_slots.find(
    (s) => s.device_type.label === 'Main Camera',
  );

  if (useCase?.id && slot?.id) {
    axios
      .post(route('clinician.set-device-for-session', props.sessionId), {
        use_case_id: useCase.id,
        use_case_slot_id: slot.id,
        device_data: device,
      })
      .then(() => {
        // Update predefined video pool if exist
        if (props.devices.video.length) {
          const updatedDevice = updatedDevices.video.find(
            (d) => d.device_type_label === 'Main Camera',
          );
          updatedDevice.device_data = device;
          emit('update:devices', updatedDevices);
        }

        // add to pinia
        deviceAndSessionSetupStore.manageDefaultClinicianDevices(deviceKind, device);
      })
      .catch((e) => {
        console.log(e);
      });
  }
};

watch(
  () => currentSpeaker.value,
  (newSpeakerId) => {
    if (newSpeakerId === 'empty') return;

    try {
      speakerStatus.value = 'default';

      const selectedSpeaker = speakers.value.find((s) => s.deviceId === currentSpeaker.value);
      spkLabel.value = selectedSpeaker.label;

      if (props.isClinician) {
        // update status
        if (
          predefinedSpk?.device_data?.label !== spkLabel.value ||
          !predefinedSpk?.device_data?.label?.includes(spkLabel.value)
        ) {
          speakerStatus.value = 'warning';
        } else {
          speakerStatus.value = 'success';
        }
      } else {
        speakerStatus.value = 'success';
      }

      multiCameraDevicesStore.cameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].speakerDeviceId =
        newSpeakerId;
    } catch (error) {
      console.error('Speakers switch failed:', error);
      speakerStatus.value = 'fail';
    }
  },
);

const setupAudioVisualization = (stream) => {
  try {
    const audioContext = new AudioContext();
    const microphone = audioContext.createMediaStreamSource(stream);

    if (javascriptNode) {
      javascriptNode.onaudioprocess = null;
      javascriptNode.disconnect();
    }

    javascriptNode = audioContext.createScriptProcessor(1024, 1, 1);
    microphone.connect(javascriptNode);
    javascriptNode.connect(audioContext.destination);

    let maxLevelL = 0.5;
    let oldLevelL = 0.1;

    javascriptNode.onaudioprocess = (event) => {
      if (isUnmounted.value) return;

      const inputL = event.inputBuffer.getChannelData(0);
      let instantL;
      let sumL = 0.0;
      for (let i = 0; i < inputL.length; ++i) {
        sumL += inputL[i] * inputL[i];
      }
      instantL = Math.sqrt(sumL / inputL.length);
      maxLevelL = Math.max(maxLevelL, instantL);
      instantL = Math.max(instantL, oldLevelL - 0.008);
      oldLevelL = instantL;
      currentWidth.value = Math.max(0, Math.min(100, (instantL / maxLevelL) * 100));
    };
  } catch (error) {
    console.error('Audio visualization setup failed:', error);
  }
};

watch(
  cameras,
  (newCameras) => {
    if (newCameras.length > 0) {
      const cachedCamId = multiCameraDevicesStore.cameraTypes['Internal Webcam'].cameraDeviceId;
      const predefinedCamId = predefinedCam?.device_data?.deviceId;
      const cachedCamAvailable = newCameras.find((cam) => cam.deviceId === cachedCamId);
      const predefinedCamAvailable = newCameras.find((cam) => cam.deviceId === predefinedCamId);

      if (predefinedCamAvailable) {
        currentCamera.value = predefinedCamId;
      } else if (cachedCamAvailable) {
        currentCamera.value = cachedCamId;
      } else {
        currentCamera.value = newCameras[0].deviceId;
      }
    }
  },
  { immediate: true },
);

watch(
  microphones,
  (newMicrophones) => {
    if (newMicrophones.length > 0) {
      // filter default doubles
      newMicrophones = newMicrophones.filter(
        (device) => device.deviceId !== 'default' && device.deviceId !== 'communications',
      );

      const cachedMicId = multiCameraDevicesStore.cameraTypes['Internal Webcam'].micDeviceId;
      const predefinedMicId = predefinedMic?.device_data?.deviceId;
      const cachedMicAvailable = newMicrophones.find((mic) => mic.deviceId === cachedMicId);
      const predefinedMicAvailable = newMicrophones.find((mic) => mic.deviceId === predefinedMicId);

      if (predefinedMicAvailable) {
        currentMicro.value = predefinedMicId;
      } else if (cachedMicAvailable) {
        currentMicro.value = cachedMicId;
      } else {
        currentMicro.value = newMicrophones[0].deviceId;
      }
    }
  },
  { immediate: true },
);

watch(
  speakers,
  (newSpeakers) => {
    if (newSpeakers.length > 0) {
      const cachedSpkId = multiCameraDevicesStore.cameraTypes['Internal Webcam'].speakerDeviceId;
      const predefinedSpkId = predefinedSpk?.device_data?.deviceId;

      const cachedSpeakerAvailable = newSpeakers.find((spk) => spk.deviceId === cachedSpkId);
      const predefinedSpkAvailable = newSpeakers.find((spk) => spk.deviceId === predefinedSpkId);

      if (predefinedSpkAvailable) {
        currentSpeaker.value = predefinedSpkId;
      } else if (cachedSpeakerAvailable) {
        currentSpeaker.value = cachedSpkId;
      } else {
        currentSpeaker.value = newSpeakers[0].deviceId;
      }

      spkLabel.value = currentSpeaker.value;
    }
  },
  { immediate: true },
);

const playSpeakerTest = async () => {
  if (isSpeakerPlaying.value) return;
  isSpeakerPlaying.value = true;

  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const dest = ctx.createMediaStreamDestination();
  const audioEl = speakerTestOutput.value;
  audioEl.srcObject = dest.stream;

  if (typeof audioEl.setSinkId === 'function' && currentSpeaker.value) {
    await audioEl.setSinkId(currentSpeaker.value).catch(console.warn);
  }

  await audioEl.play();

  const notes = [
    { freq: 523.25, duration: 0.15 },
    { freq: 659.25, duration: 0.15 },
    { freq: 783.99, duration: 0.15 },
    { freq: 659.25, duration: 0.15 },
    { freq: 523.25, duration: 0.25 },
  ];

  let startTime = ctx.currentTime + 0.05;

  notes.forEach(({ freq, duration }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(dest);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.4, startTime + 0.02);
    gain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.start(startTime);
    osc.stop(startTime + duration);

    startTime += duration + 0.05;
  });

  const totalDuration = notes.reduce((acc, n) => acc + n.duration + 0.05, 0.05);

  setTimeout(() => {
    isSpeakerPlaying.value = false;
    audioEl.srcObject = null;
    ctx.close();
  }, totalDuration * 1000 + 100);
};

onBeforeUnmount(async () => {
  isUnmounted.value = true;

  if (props.isClinician && setupWarnings.value && props.proceed) {
    await saveNewDeviceSelection();
  }

  await stopVideoStream();

  if (audioStream?.value?.active) {
    await stopLiveStream(audioStream);
  }

  if (javascriptNode) {
    javascriptNode.onaudioprocess = null;
    javascriptNode.disconnect();
    javascriptNode = null;
  }
});

// Save new selections for the current session
const saveNewDeviceSelection = async () => {
  if (cameraStatus.value === 'warning' && props.useCases) {
    saveMainCameraDevice(currentCamera.value, DEVICES_KINDS.VIDEO_INPUT);
  }
  if (micStatus.value === 'warning') {
    saveDefaultDevice(currentMicro.value, DEVICES_KINDS.AUDIO_INPUT);
  }
  if (speakerStatus.value === 'warning') {
    saveDefaultDevice(currentSpeaker.value, DEVICES_KINDS.AUDIO_OUTPUT);
  }
};
</script>

<template>
  <div class="w-full inline-flex gap-4">
    <!-- VIDEO -->
    <div class="w-1/2 rounded-lg border border-gray-200">
      <!-- VIDEO:Header -->
      <div
        class="
          inline-flex
          justify-between
          items-center
          w-full
          p-5
          2xl:py-4
          sm:px-6
          rounded-lg
          bg-gray-50
        "
      >
        <!-- Tile title -->
        <span class="text-gray-500 font-bold uppercase">video</span>
        <!-- Tile guide (if use case "General") -->
        <div v-if="false" class="flex w-full items-center justify-center">
          <ToggleSettings :enabled="showGuide" @toggled="showGuide = !showGuide" class="ml-2" />
          <span class="block text-sm text-gray-500 ml-2">Show guide</span>
        </div>
        <!-- Tile status icon -->
        <div class="flex items-center">
          <span v-if="cameraStatus === 'success'">
            <CheckIcon
              style="background-color: rgb(62 183 166)"
              class="transition text-white rounded-full"
            />
          </span>
          <span v-else-if="cameraStatus === 'warning'">
            <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
          </span>
          <span v-else-if="cameraStatus === 'fail'">
            <CrossIcon class="transition bg-red-100 text-red-800 rounded-full" />
          </span>
          <span v-else class="w-5" />
        </div>
      </div>

      <!-- VIDEO:Body -->
      <div>
        <!-- Video block -->
        <div class="rounded-xl flex w-full">
          <div class="flex-col h-full w-full">
            <div class="relative w-full h-auto mb-5">
              <div class="aspect-video w-full overflow-hidden">
                <video
                  ref="video"
                  muted
                  autoplay
                  class="flex w-full object-cover border-t border-b"
                />
              </div>
              <!-- Guidance overlay -->
              <div v-if="showGuide" class="absolute flex top-1 h-full w-full p-3 gap-4">
                <div
                  class="
                    relative
                    inline-block
                    rounded-3xl
                    w-1/2
                    h-full
                    border-3 border-dashed border-vice-teal-500
                  "
                >
                  <span
                    class="
                      absolute
                      w-10
                      -top-3
                      left-1/2
                      ml-negative-18px
                      z-10
                      bg-vice-teal-500
                      font-bold
                      uppercase
                      text-xxs text-white
                      px-2
                      py-0.5
                      rounded-full
                      flex
                      justify-center
                      tracking-wider
                    "
                  >
                    you
                  </span>
                  <div class="rounded-3xl w-full h-full bg-vice-teal-100 opacity-40" />
                </div>
                <div
                  class="
                    relative
                    inline-block
                    rounded-3xl
                    w-1/2
                    h-full
                    border-3 border-dashed border-vice-teal-500
                  "
                >
                  <span
                    class="
                      absolute
                      w-18
                      -top-3
                      left-1/2
                      ml-negative-33px
                      z-10
                      bg-vice-teal-500
                      font-bold
                      uppercase
                      text-xxs text-white
                      px-2
                      py-0.5
                      rounded-full
                      flex
                      justify-center
                      tracking-wider
                    "
                  >
                    patient
                  </span>
                  <div class="rounded-3xl w-full h-full bg-vice-teal-100 opacity-40" />
                </div>
              </div>

              <!-- Loading/NotAvailable device overlay -->
              <div
                v-if="!videoStream || loading"
                class="
                  z-20
                  absolute
                  bg-gray-700
                  top-0
                  left-0
                  w-full
                  h-full
                  flex flex-col
                  items-center
                  text-sm
                  justify-center
                  min-h-58
                "
              >
                <div v-if="loading">
                  <span class="flex items-center">
                    <svg
                      class="animate-spin -ml-1 h-8 w-8"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#14B8A6"
                        stroke-width="4"
                      ></circle>
                      <path
                        class=""
                        fill="#14B8A6"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </span>
                </div>
                <div v-if="cameras.length < 1 && !loading" class="space-y-2">
                  <div class="flex text-center w-full justify-center">
                    <CameraPermissionBlockedIcon class="h-20 w-20" />
                  </div>
                  <div class="flex text-center w-full justify-center">
                    <span class="text-white text-center">
                      A
                      <span class="font-bold">camera</span>
                      is needed to join the VCE session
                    </span>
                  </div>
                </div>
                <div v-if="cameras.length >= 1 && !loading" class="space-y-2">
                  <div class="flex text-center w-full justify-center">
                    <CameraPermissionBlockedIcon class="h-20 w-20" />
                  </div>
                  <div class="flex text-center w-full justify-center">
                    <span class="text-white mx-4 text-center">
                      It looks like
                      <span class="font-bold">another app is using your camera</span>
                      now. Please close any other apps that might be using your microphone and
                      camera.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!--  Camera  -->
            <div
              class="flex-col p-2 mx-2 my-3 items-center rounded-lg"
              :class="{ 'pt-24': !videoStream }"
            >
              <div class="flex items-center">
                <!-- Camera status icon -->
                <span v-if="cameraStatus === 'success'">
                  <CheckIcon
                    style="background-color: rgb(62 183 166)"
                    class="transition text-white rounded-full"
                  />
                </span>

                <span v-else-if="cameraStatus === 'warning'">
                  <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
                </span>
                <span v-else-if="cameraStatus === 'fail'">
                  <CrossIcon class="transition bg-red-100 text-red-800 rounded-full" />
                </span>
                <span v-else class="w-5" />

                <!-- Dropdown -->
                <div class="relative">
                  <label class="flex h-4 leading-4 text-xxs font-semibold text-vice-teal-900 pl-2">
                    <select
                      id="select-camera"
                      v-model="currentCamera"
                      :class="[{ ' highlighted ': highlighted && cameraStatus === 'warning' }]"
                      class="
                        bg-none
                        w-full
                        pr-10
                        cursor-pointer
                        bg-white
                        p-0
                        leading-4
                        focus:ring-0
                        focus:border-0
                        flex
                        relative
                        text-xs
                        font-semibold
                        border-0
                        rounded-md
                      "
                    >
                      <option
                        v-for="camera of cameras"
                        :key="camera.deviceId"
                        :value="camera.deviceId"
                      >
                        {{ camera.label }}
                      </option>
                    </select>
                    <!-- Select arrow icon -->
                    <div
                      class="
                        pointer-events-none
                        absolute
                        inset-y-0
                        right-0
                        flex
                        items-center
                        border border-gray-500
                        rounded
                      "
                    >
                      <svg
                        class="w-5 h-5 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="showGuide" class="border-t mt-4" />
            <!-- Guidance -->
            <div v-if="showGuide" class="flex-col p-3 mx-2 my-2 items-center">
              <div class="relative">
                <div class="flex items-center mb-3">
                  <UsersIcon class="h-4 w-4 text-vice-teal-600 mr-2" />
                  <h4 class="uppercase text-vice-teal-600 text-xxs font-bold tracking-widest">
                    Position your camera
                  </h4>
                </div>

                <div class="inline-flex items-center justify-center w-full">
                  <div class="flex w-1/2">
                    <ul class="flex flex-col gap-1 text-xxs text-gray-600 list-disc pl-5">
                      <li class="text-vice-teal-500 mb-1">
                        <span class="rounded-full bg-green-500" />
                        <span class="text-gray-800">
                          Place the camera so you and the patient are both in shot, side by side.
                        </span>
                      </li>
                      <li class="text-vice-teal-500 mb-1">
                        <span class="rounded-full bg-green-500" />
                        <span class="text-gray-800">
                          Angle it to capture where you'll both sit — not pointed straight at one
                          chair.
                        </span>
                      </li>
                      <li class="text-vice-teal-500 mb-1">
                        <span class="rounded-full bg-green-500" />
                        <span class="text-gray-800">
                          Step back if needed so there's room for two people in the frame.
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div class="flex w-1/2">
                    <svg viewBox="0 0 160 96" class="w-full h-auto" role="img">
                      <path
                        d="M80 86 L18 22 L142 22 Z"
                        fill="#0d9488"
                        fill-opacity="0.08"
                        stroke="#0d9488"
                        stroke-opacity="0.5"
                        stroke-dasharray="3 3"
                        stroke-width="1"
                      />
                      <rect x="72" y="80" width="16" height="10" rx="2" fill="#0d9488" />
                      <circle cx="80" cy="85" r="2" fill="white" />
                      <circle
                        cx="48"
                        cy="44"
                        r="9"
                        fill="none"
                        stroke="#0d9488"
                        stroke-width="1.5"
                      />
                      <path
                        d="M40 60 Q48 52 56 60"
                        fill="none"
                        stroke="#0d9488"
                        stroke-width="1.5"
                      />
                      <text
                        x="48"
                        y="74"
                        text-anchor="middle"
                        font-size="8"
                        fill="#0d9488"
                        font-weight="600"
                      >
                        You
                      </text>
                      <circle
                        cx="112"
                        cy="44"
                        r="9"
                        fill="none"
                        stroke="#0d9488"
                        stroke-width="1.5"
                      />
                      <path
                        d="M104 60 Q112 52 120 60"
                        fill="none"
                        stroke="#0d9488"
                        stroke-width="1.5"
                      />
                      <text
                        x="112"
                        y="74"
                        text-anchor="middle"
                        font-size="8"
                        fill="#0d9488"
                        font-weight="600"
                      >
                        Patient
                      </text>
                      <path
                        d="M58 42 Q80 32 102 42"
                        fill="none"
                        stroke="#0d9488"
                        stroke-opacity="0.4"
                        stroke-width="1"
                        stroke-dasharray="2 2"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AUDIO -->
    <div class="w-1/2 rounded-lg border border-gray-200">
      <!-- AUDIO:Header -->
      <div class="inline-flex w-full justify-between p-5 2xl:py-4 sm:px-6 rounded-lg bg-gray-50">
        <span class="text-gray-500 font-bold uppercase">audio</span>
        <div class="flex">
          <span v-if="micStatus === 'success'" class="pt-1">
            <CheckIcon
              style="background-color: rgb(62 183 166)"
              class="transition text-white rounded-full"
            />
          </span>
          <span v-else-if="micStatus === 'warning'" class="pt-1">
            <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
          </span>
          <span v-else-if="micStatus === 'fail'" class="pt-1">
            <CrossIcon class="transition bg-red-100 text-red-800 rounded-full" />
          </span>
          <span v-else class="w-5" />
        </div>
      </div>
      <!-- AUDIO:Body -->
      <div class="flex flex-col gap-2 text-left rounded-b-lgbg-white">
        <div class="rounded-xl relative w-full h-0" style="padding-top: 75%">
          <div class="absolute h-full w-full bottom-0 left-0">
            <!-- Microphone -->
            <div class="flex flex-col gap-3 h-1/2 border-t p-6 bg-white">
              <div class="w-full h-full rounded-lg p-1 inline-flex justify-between items-center">
                <!-- Mic selection -->
                <div class="flex w-full items-center gap-2">
                  <!-- Mic icon -->
                  <div
                    class="
                      rounded-full
                      w-10
                      h-10
                      flex
                      justify-center
                      items-center
                      relative
                      bg-vice-teal-600
                    "
                  >
                    <img src="/image/icons/microphone.svg" alt="mic" width="20" height="20" />
                  </div>

                  <!-- Dropdown -->
                  <div class="flex w-62 flex-col ml-2">
                    <span class="mb-1 2xl:mb-4 text-gray-400 font-bold uppercase">microphone</span>
                    <div class="relative">
                      <label class="flex h-4 text-vice-teal-900">
                        <select
                          id="select-microphone"
                          v-model="currentMicro"
                          :class="[
                            {
                              ' highlighted ':
                                highlighted && (micStatus === 'warning' || !audioInTheSameGroup),
                            },
                          ]"
                          class="
                            cursor-pointer
                            bg-none
                            w-62
                            p-0
                            pr-7
                            leading-4
                            focus:ring-0
                            focus:border-0
                            flex
                            absolute
                            text-xs
                            font-semibold
                            border-0
                            rounded-md
                          "
                        >
                          <option
                            v-for="microphone of microphones"
                            :key="microphone.deviceId"
                            :value="microphone.deviceId"
                          >
                            {{ microphone.label }}
                          </option>
                        </select>
                        <!-- Select arrow icon -->
                        <div
                          class="
                            pointer-events-none
                            absolute
                            inset-y-0
                            right-0
                            flex
                            items-center
                            border border-gray-500
                            rounded
                          "
                        >
                          <svg
                            class="w-5 h-5 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="3"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Mic status icon -->
                <div class="flex">
                  <span v-if="micStatus === 'success'" class="pt-1">
                    <CheckIcon
                      style="background-color: rgb(62 183 166)"
                      class="transition text-white rounded-full"
                    />
                  </span>
                  <span v-if="micStatus === 'warning'" class="pt-1">
                    <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
                  </span>
                  <span v-if="micStatus === 'fail'" class="pt-1">
                    <CrossIcon class="transition bg-red-100 text-red-800 rounded-full" />
                  </span>
                </div>
              </div>

              <!-- Mic level -->
              <div class="flex-col">
                <span class="p-2 text-xs text-gray-400">Mic level</span>
                <div class="rounded overflow-hidden relative w-auto mx-1 h-0 pb-3 pt-3">
                  <div
                    v-if="micEnabled"
                    class="
                      absolute
                      bg-white
                      rounded-lg
                      h-3
                      left-0
                      right-0
                      top-1/2
                      transform
                      -translate-y-1/2
                      overflow-hidden
                      border border-gray-300
                    "
                  >
                    <div
                      class="absolute bg-vice-teal-400 rounded-lg h-full top-0 left-0"
                      :style="{ width: `${currentWidth}%` }"
                    />
                  </div>
                  <div
                    v-if="!micEnabled"
                    class="
                      absolute
                      top-0
                      left-0
                      w-full
                      h-full
                      flex flex-col
                      items-center
                      text-sm
                      justify-center
                      z-10
                    "
                  >
                    <span class="flex items-center">
                      <svg
                        class="animate-spin -ml-1 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#14B8A6"
                          stroke-width="4"
                        />
                        <path
                          class=""
                          fill="#14B8A6"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Speakers -->
            <div class="flex flex-col gap-4 h-1/2 rounded-b-lg border-t p-6 bg-white">
              <div class="w-full h-full rounded-lg p-1 inline-flex justify-between items-center">
                <!-- Speaker selection -->
                <div class="flex w-full items-center gap-2">
                  <!-- Speaker icon -->
                  <div
                    class="
                      rounded-full
                      w-10
                      h-10
                      flex
                      justify-center
                      items-center
                      relative
                      bg-vice-teal-600
                    "
                  >
                    <img src="/image/icons/speaker.svg" alt="speakers" width="20" height="20" />
                  </div>

                  <!-- Dropdown -->
                  <div class="flex w-62 flex-col ml-2">
                    <span class="mb-1 2xl:mb-4 text-gray-400 font-bold uppercase">speakers</span>
                    <div class="relative">
                      <label class="flex h-4 text-vice-teal-900">
                        <select
                          id="select-speaker"
                          v-model="currentSpeaker"
                          :class="[
                            {
                              ' highlighted ':
                                highlighted &&
                                (speakerStatus === 'warning' || !audioInTheSameGroup),
                            },
                          ]"
                          class="
                            cursor-pointer
                            bg-none
                            w-62
                            p-0
                            pr-7
                            leading-4
                            focus:ring-0
                            focus:border-0
                            flex
                            absolute
                            text-xs
                            font-semibold
                            border-0
                            rounded-md
                          "
                        >
                          <option
                            v-for="speaker of speakers"
                            :key="speaker.deviceId"
                            :value="speaker.deviceId"
                          >
                            {{ speaker.label }}
                          </option>
                        </select>
                        <!-- Select arrow icon -->
                        <div
                          class="
                            pointer-events-none
                            absolute
                            inset-y-0
                            right-0
                            flex
                            items-center
                            border border-gray-500
                            rounded
                          "
                        >
                          <svg
                            class="w-5 h-5 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="3"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Speaker status icon -->
                <div class="flex">
                  <span v-if="speakerStatus === 'success'" class="pt-1">
                    <CheckIcon
                      style="background-color: rgb(62 183 166)"
                      class="transition text-white rounded-full"
                    />
                  </span>
                  <span v-if="speakerStatus === 'warning'" class="pt-1">
                    <CheckIcon class="transition text-white rounded-full bg-vice-yellow" />
                  </span>
                  <span v-if="speakerStatus === 'fail'" class="pt-1">
                    <CrossIcon class="transition bg-red-100 text-red-800 rounded-full" />
                  </span>
                </div>
              </div>
              <!-- Speaker test -->
              <div class="flex-col">
                <audio ref="speakerTestOutput" class="hidden" />
                <div
                  class="rounded overflow-hidden relative w-auto mx-1 pb-1 flex items-center gap-3"
                >
                  <button
                    @click="playSpeakerTest"
                    :disabled="isSpeakerPlaying"
                    class="
                      w-36
                      flex
                      items-center
                      gap-2
                      px-3
                      py-1.5
                      text-xs
                      font-medium
                      rounded-lg
                      border
                      transition-all
                      leading-6
                    "
                    :class="
                      isSpeakerPlaying
                        ? 'bg-vice-teal-50 border-vice-teal-400 text-vice-teal-600 cursor-not-allowed'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-vice-teal-400 hover:text-vice-teal-600'
                    "
                  >
                    <span v-if="!isSpeakerPlaying">
                      <!-- Speaker icon -->
                      <span class="rounded-full w-6 h-6 flex justify-center items-center relative">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 22"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M14.7513 21.9753V19.884C16.6974 19.2649 18.2649 18.1406 19.4541 16.5109C20.6434 14.8815 21.2381 13.0445 21.2381 11C21.2381 8.95544 20.6434 7.11847 19.4541 5.48904C18.2649 3.8594 16.6974 2.73504 14.7513 2.11597V0.0246582C17.2629 0.697475 19.3093 2.04581 20.8903 4.06965C22.4712 6.0935 23.2616 8.40361 23.2616 11C23.2616 13.5963 22.4712 15.9065 20.8903 17.9303C19.3093 19.9542 17.2629 21.3025 14.7513 21.9753ZM0.740234 14.4068V7.66063H5.74791L11.5337 1.87479V20.1926L5.74791 14.4068H0.740234ZM14.7513 15.9635V6.03649C15.6612 6.53121 16.3643 7.22787 16.8608 8.12645C17.3571 9.02504 17.6053 9.99413 17.6053 11.0337C17.6053 12.0594 17.3549 13.0137 16.8541 13.8968C16.3533 14.7799 15.6524 15.4687 14.7513 15.9635ZM9.5099 6.78363L6.60905 9.68448H2.76374V12.3829H6.60905L9.5099 15.2838V6.78363Z"
                            fill="#0d9488"
                          />
                        </svg>
                      </span>
                    </span>
                    <span v-else>
                      <!-- Visual bars while playing -->
                      <span v-if="isSpeakerPlaying" class="flex items-end gap-0.5 h-4">
                        <span
                          v-for="i in 5"
                          :key="i"
                          class="w-1 bg-vice-teal-400 rounded-full animate-bounce"
                          :style="{
                            animationDelay: `${i * 0.1}s`,
                            height: `${[60, 100, 80, 100, 60][i - 1]}%`,
                          }"
                        />
                      </span>
                    </span>
                    {{ isSpeakerPlaying ? 'Playing...' : 'Speaker test' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.left-18 {
  left: 4.75rem;
}

.left-25 {
  left: 6.25rem;
}

.w-68 {
  width: 16.5rem;
}

.leading-065 {
  line-height: 0.65rem;
}

video {
  transform: rotateY(180deg);
  -webkit-transform: rotateY(180deg);
  -moz-transform: rotateY(180deg);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.highlighted {
  border: 1px solid #ffdd00;
  box-shadow: 0 0 7px 2px rgb(255 214 0);
  transition: all 0.5s ease;
}

select::-ms-expand {
  display: none;
}
</style>
